/**
 * API base for PaySafeMobile — same rules as frontend src/config/api.js
 * Dev: use Vite proxy /api → backend (see vite.config.js)
 *
 * Named payoutMobileApi.js (not api.js) so dev URLs are not /api.js — that path
 * collides with the proxy prefix `/api` and was forwarded to the backend.
 */

function detectApiBase() {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "";
  // Same-origin /api behind nginx (paysafego.appstaging.biz, tenant *.appstaging.biz, etc.)
  if (host.endsWith("appstaging.biz")) return window.location.origin;
  if (host.endsWith("paysafego.net")) {
    if (host === "api.paysafego.net") return "https://api.paysafego.net";
    return window.location.origin;
  }
  return "";
}

export function getApiBase() {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const override = params.get("apiBase");
  const forceLocal = params.get("useLocalApi") === "1" || params.get("localApi") === "1";
  if (forceLocal) return "http://localhost:4400";
  if (override) return override;
  const env = import.meta.env?.VITE_API_BASE;
  if (env) return env.replace(/\/$/, "");
  return detectApiBase();
}

export function apiUrl(path) {
  const base = getApiBase();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!base) return p;
  return `${base}${p}`;
}

export async function fetchJson(path, options = {}) {
  const url = apiUrl(path);
  const res = await fetch(url, {
    ...options,
    headers: { Accept: "application/json", ...(options.headers || {}) },
  });
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const err = new Error(data.error || res.statusText || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/** GET /api/venues/:id/payout-policy — public */
export async function fetchPayoutPolicy(venueId) {
  const j = await fetchJson(`/api/venues/${encodeURIComponent(venueId)}/payout-policy`);
  if (!j.success) throw new Error(j.error || "Failed to load payout policy");
  return j.data;
}

/** GET /api/venues — public */
export async function fetchVenuesList() {
  const j = await fetchJson("/api/venues");
  if (!j.success) throw new Error(j.error || "Failed to load venues");
  return j.data || [];
}
